import { createRng, hashSeed } from '../rng.js';
import { createBus, EVENT } from '../events.js';
import { createPerception } from '../perception.js';
import { createSuspicion } from '../suspicion.js';
import { ft } from '../../core/units.js';

/**
 * Quick Hands, as a simulation.
 *
 * A tray of mini chimis sits on the pass and the chefs keep walking past it.
 * You reach in when nobody is looking and you hold still when somebody is.
 * Being caught ends the round, and what "caught" means is the same thing it
 * means everywhere else in this building.
 *
 * **It uses the real perception.** Not a cone written for a minigame, not a
 * "is anyone looking" boolean: this file builds four bodies with vision rows on
 * them, hands them to `createPerception`, and asks the same module the rest of
 * the game asks. The reach is offered as *conduct* — a state, so a chef who
 * turns round mid-grab gets surer the longer they watch, exactly as somebody
 * would watching you stand in a restricted room — and the grab itself goes out
 * as an `EVENT.TAKE` on a bus, witnessed by the same path a stolen linen is.
 *
 * That is the whole reason this class is worth having. A second detector would
 * teach the player a second set of rules, and then the thing they learned here
 * would be wrong out there. What they learn here is the real one: the cone is
 * this wide, it falls off with distance, and a witness who keeps looking gets
 * surer.
 *
 * Feet throughout, converted at the edges, because the cone rows in
 * `staff.js` are written in feet and these have to be readable next to them.
 */

/** The pass, in feet. The player works the near side of it. */
export const PASS = Object.freeze({ width: 40, depth: 18 });

/** Where the player stands, and where the tray sits in front of them. */
export const STATION = Object.freeze({ x: 20, z: 13 });
export const CHIMIS = Object.freeze({ x: 20, z: 10 });

/** The line the chefs walk, on the far side of the pass. */
const LANE_Z = 3;

/**
 * How long a reach takes, and how long a chimi has to be in the air.
 *
 * Three quarters of a second, which is long enough that a chef who turns round
 * at the wrong moment is a real risk and short enough that a gap between two
 * patrols is a gap you can use. The whole game is reading that gap.
 */
const GRAB_SECONDS = 0.75;

/**
 * What a chef can see and hear. The same shape the roster writes.
 *
 * Thirteen feet, which is short for a person and is the whole geometry of the
 * class: the player stands ten feet off the line, so only a chef within about
 * seven feet of them along it can reach them at all. That is what makes a gap
 * a *place* rather than a moment — somebody at the far end of their length is
 * not a danger however they are facing, and somebody opposite you is one
 * however far away they are looking. The first cut gave them the roster's
 * twenty-six and two chefs covered the whole pass between them: at the second
 * level the longest clear moment in a forty-five second round was four tenths
 * of a second, against a reach that takes three quarters of one.
 */
const CHEF_SIGHT = Object.freeze({ range: 13, halfAngle: 55, dwell: 1.2 });

export function createHands({ row, seed = 'hands' }) {
  const rng = createRng(hashSeed(seed, row.level));

  /**
   * A chef, as far as perception is concerned.
   *
   * Exactly the surface `createPerception` reads and nothing else: a data row
   * with a vision cone on it, a position, and a `sees` that is range, then
   * angle, then a clear line. There is no wall on the pass, so the third test
   * is free — which is why this can be forty lines rather than four hundred.
   */
  function chef(index) {
    /*
     * Where each of them works, and when.
     *
     * Spaced out from the middle of the pass rather than spread evenly across
     * it, because the player stands at the middle: an even spread with an odd
     * number of chefs parks one of them permanently opposite the tray, and a
     * cone that never leaves is not a patrol, it is a wall. And the phases are
     * dealt round the cycle rather than rolled, so two chefs are on opposite
     * swings and the pass has a rhythm to it. Learning that rhythm is the game;
     * a roll would be a game of waiting.
     */
    /*
     * Half a step either side of the middle, alternating outward, so no chef's
     * beat is centred on the tray. An even spread does exactly that with an odd
     * number of them, and a cone that is always over the tray is not a patrol,
     * it is a wall — the first cut of this made level one, with its single
     * chef, the only unplayable level in the game.
     */
    const step = ((index % 2 === 0 ? -1 : 1) * (Math.floor(index / 2) + 0.5)) * row.spacing;
    const edge = row.swing + 2.5;
    const home = Math.max(edge, Math.min(PASS.width - edge, PASS.width / 2 + step));
    const phase = (index / row.chefs) * Math.PI * 2 + (rng.next() - 0.5) * 0.5;
    const speed = row.pace * (0.92 + rng.next() * 0.16);
    /** Metres, because that is what perception measures in. */
    const position = { x: ft(home), y: 0, z: ft(LANE_Z) };
    let yaw = 0;
    let clock = 0;

    return {
      data: {
        id: `chef-${index}`,
        department: 'kitchen',
        vision: CHEF_SIGHT,
        hearing: 24,
      },
      get position() {
        return position;
      },
      get yaw() {
        return yaw;
      },
      present: true,
      hasVision: true,
      /*
       * Range, then the cone, and that is all.
       *
       * The building's own `sees` ends with a line-of-sight test against the
       * world. There is no world here — the pass is an open counter, and a
       * training room with a wall down the middle of it would be a different
       * lesson — so it ends at the angle. Everything above it is the same
       * arithmetic, in the same order, for the same reason.
       */
      sees(point) {
        const dx = point.x - position.x;
        const dz = point.z - position.z;
        const distance = Math.hypot(dx, dz);
        if (distance > ft(CHEF_SIGHT.range)) return false;
        if (distance < 1e-3) return true;
        const facing = (dx / distance) * Math.sin(yaw) + (dz / distance) * Math.cos(yaw);
        return facing >= Math.cos((CHEF_SIGHT.halfAngle * Math.PI) / 180);
      },
      step(dt) {
        clock += dt;
        const along = Math.sin(clock * speed + phase);
        position.x = ft(home + along * row.swing);

        /*
         * Which way they are facing is the whole game, and it is tied to how
         * fast they are walking.
         *
         * `turning` is the velocity of the stride, so it is at its greatest in
         * the middle of a length and nothing at all at each end. A chef mid
         * stride is looking where they are going, which is along the line and
         * away from the counter; a chef at the turn has stopped and is looking
         * across it. So the gap you reach into is the middle of somebody's
         * length, and the moment they run out of line is the moment to have
         * your hand back.
         *
         * `glance` is how far their eyes come off the pass at full stride: one
         * is a square quarter turn and nothing is a chef who never looks away,
         * which is what the last level is.
         */
        const turning = Math.cos(clock * speed + phase);
        yaw = (Math.PI / 2) * turning * row.glance;
      },
    };
  }

  const chefs = Array.from({ length: row.chefs }, (_, index) => chef(index));
  const bus = createBus();
  let elapsed = 0;
  /*
   * Perception's own clock is game minutes and it uses it for one thing: how
   * long a person stays on something, which is forty-five minutes. A round is
   * three quarters of one, so nothing in here is ever forgotten — which is the
   * right answer for a round this short and is said out loud rather than left
   * to a zero that happens to work.
   */
  const perception = createPerception({ npcs: chefs, bus, now: () => elapsed / 60 });
  const stop = perception.listen();
  /*
   * And the second half of the real thing, which is the half that makes the
   * lesson a game: perception says what somebody believes, `suspicion.js` says
   * how wound up they are about it, and standing there behaving yourself in
   * plain view *drains* it faster than being looked at fills it. That is the
   * alibi, it is the whole counterplay in the building, and without it a
   * glimpse here would be a death sentence — a claim that only ever goes up
   * means every round ends the same way, given long enough.
   *
   * So "hold still when somebody is looking" is not advice this file invented.
   * It is `ALIBI` beating `DURATION_GAIN`, which is an invariant `suspicion.js`
   * keeps and has a test for.
   */
  const suspicion = createSuspicion({ npcs: chefs, perception });

  let taken = 0;
  let left = row.seconds;
  let reaching = 0;
  let wantReach = false;
  let caught = null;

  /** How wound up the most wound-up of them is. Suspicion's number, not ours. */
  function heat() {
    return suspicion.worst;
  }

  return {
    get level() {
      return row.level;
    },
    get target() {
      return row.target;
    },
    get score() {
      return caught ? 0 : taken;
    },
    get taken() {
      return taken;
    },
    get caught() {
      return caught;
    },
    get heat() {
      return heat();
    },
    /** How much of the reach is done, 0..1, for the screen. */
    get reaching() {
      return reaching / GRAB_SECONDS;
    },
    get secondsLeft() {
      return Math.max(0, left);
    },
    /** In feet, for whatever draws them. Perception keeps the metres. */
    get chefs() {
      return chefs.map((who) => ({
        id: who.data.id,
        x: who.position.x / ft(1),
        z: who.position.z / ft(1),
        yaw: who.yaw,
        range: CHEF_SIGHT.range,
        halfAngle: CHEF_SIGHT.halfAngle,
        /** Whether this one has the player, asked of perception and nobody else. */
        looking: perception.sees(who.data.id),
      }));
    },
    get watched() {
      return perception.watched;
    },
    get done() {
      return Boolean(caught) || left <= 0 || taken >= row.chimis;
    },
    get passed() {
      return !caught && taken >= row.target;
    },

    /** Hold it down to reach in, let go to stand up straight. */
    hold(on) {
      wantReach = Boolean(on);
    },

    step(dt) {
      if (this.done) return;
      elapsed += dt;
      left -= dt;
      for (const who of chefs) who.step(dt);

      // Reaching is a state somebody can watch you be in, so that is what it
      // is offered to perception as. Standing up straight is not conduct.
      if (wantReach && taken < row.chimis) reaching += dt;
      else reaching = 0;

      const standing = { x: ft(STATION.x), y: 0, z: ft(STATION.z) };
      perception.observe(dt, {
        position: standing,
        conduct: reaching > 0 ? [{ kind: 'take', detail: 'chimi' }] : [],
      });
      /*
       * Never sprinting and never holding the evidence, because there is
       * nowhere to run to and the chimi goes straight in your pocket. So the
       * player is always "behaving" in the sense `suspicion.js` means, and the
       * alibi is always available — which is the point. The only thing that
       * fills a chef up is being seen with your hand in the tray.
       */
      suspicion.update(dt, { sprinting: false, holding: null, position: standing });

      if (reaching >= GRAB_SECONDS) {
        reaching = 0;
        taken += 1;
        /*
         * And the grab itself, on the bus, as the moment it is. Conduct is
         * being seen *reaching*; this is being seen *take one*, and they are
         * two different claims about the same half second — which is exactly
         * how the rest of the game treats a thing you were doing and a thing
         * you did.
         */
        bus.emit(EVENT.TAKE, {
          actor: 'player',
          kind: 'chimi',
          position: { x: ft(CHIMIS.x), y: 0, z: ft(CHIMIS.z) },
        });
      }

      if (suspicion.worst >= row.caughtAt) {
        const who = suspicion.watching[0];
        caught = { by: who ? who.id : null, confidence: suspicion.worst, state: who ? who.state : null };
        stop();
      }
    },

    /** Lets the perception go. Called when the round is put away. */
    close() {
      stop();
    },
  };
}
